import { useState, KeyboardEvent } from 'react';
import { ProjectAccountStats, WorkspaceMember } from '../../types/accounts';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';

interface Props {
  project: ProjectAccountStats;
  members: WorkspaceMember[];
}

export function ProjectMetaBar({ project, members }: Props) {
  const { showToast } = useToast();
  const [assignedTo, setAssignedTo] = useState<string | null>(project.assigned_to);
  const [tags, setTags] = useState<string[]>(project.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [campaignId, setCampaignId] = useState<string>(project.instantly_campaign_id ?? '');
  const [campaignInput, setCampaignInput] = useState<string>(
    project.instantly_campaign_id ?? ''
  );

  const handleAssigneeChange = async (value: string) => {
    const next = value || null;
    const prev = assignedTo;
    setAssignedTo(next);
    try {
      await apiClient.updateProjectMeta(project.project_id, { assigned_to: next });
    } catch {
      setAssignedTo(prev);
      showToast('Failed to update assignee', 'error');
    }
  };

  const handleRemoveTag = async (tag: string) => {
    const next = tags.filter((t) => t !== tag);
    const prev = tags;
    setTags(next);
    try {
      await apiClient.updateProjectMeta(project.project_id, { tags: next });
    } catch {
      setTags(prev);
      showToast('Failed to update tags', 'error');
    }
  };

  const handleAddTag = async () => {
    const trimmed = tagInput.trim();
    if (!trimmed || tags.includes(trimmed)) {
      setTagInput('');
      return;
    }
    const next = [...tags, trimmed];
    const prev = tags;
    setTags(next);
    setTagInput('');
    try {
      await apiClient.updateProjectMeta(project.project_id, { tags: next });
    } catch {
      setTags(prev);
      showToast('Failed to update tags', 'error');
    }
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleCampaignIdSave = async () => {
    const next = campaignInput.trim();
    if (next === campaignId) return;
    try {
      await apiClient.updateProjectMeta(project.project_id, {
        instantly_campaign_id: next || null,
      });
      setCampaignId(next);
    } catch (error) {
      setCampaignInput(campaignId);
      const status = (error as { response?: { status?: number } })?.response?.status;
      showToast(
        status === 409
          ? 'This Instantly campaign is already registered to another project'
          : 'Failed to update Instantly campaign',
        'error'
      );
    }
  };

  const handleCampaignKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-neutral-100 mb-3">
      {/* Assignee dropdown */}
      <div className="flex items-center gap-1.5">
        <span className="text-2xs font-medium text-neutral-500 uppercase tracking-wide">
          Owner
        </span>
        <select
          id={`assignee-${project.project_id}`}
          aria-label="Project assignee"
          value={assignedTo ?? ''}
          onChange={(e) => handleAssigneeChange(e.target.value)}
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-20"
        >
          <option value="">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.name ?? m.user_id.slice(0, 8)}
            </option>
          ))}
        </select>
      </div>

      {/* Instantly campaign registration (SKY-437: declared campaign↔project link) */}
      <div className="flex items-center gap-1.5">
        <span className="text-2xs font-medium text-neutral-500 uppercase tracking-wide">
          Instantly
        </span>
        <input
          id={`instantly-campaign-${project.project_id}`}
          type="text"
          aria-label="Instantly campaign ID"
          placeholder="campaign id"
          value={campaignInput}
          onChange={(e) => setCampaignInput(e.target.value)}
          onKeyDown={handleCampaignKeyDown}
          onBlur={handleCampaignIdSave}
          spellCheck={false}
          className="text-xs font-mono text-neutral-700 placeholder-neutral-400 bg-neutral-50 border border-neutral-200 rounded px-1.5 py-0.5 w-40 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-20"
        />
      </div>

      {/* Tag chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 text-2xs font-medium bg-primary-50 text-primary-700 border border-primary-200 rounded-full px-2 py-0.5"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              onClick={() => handleRemoveTag(tag)}
              className="ml-0.5 text-primary-500 hover:text-primary-700 transition leading-none"
            >
              ×
            </button>
          </span>
        ))}

        {/* Tag input */}
        <input
          type="text"
          aria-label="Add tag"
          placeholder="+ tag"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown}
          onBlur={handleAddTag}
          className="text-2xs text-neutral-500 placeholder-neutral-400 bg-transparent border-none outline-none w-14 focus:w-24 transition-all"
        />
      </div>
    </div>
  );
}
